import type { Thunk, Action } from 'easy-peasy';
import { action, thunk } from 'easy-peasy';
import type { Injections } from '../../services/injections';
import type { ApplicationErrorOptions } from '../../../types';
import type { FetchAssetParams } from '../types';
import parseStatusCode from '../../errors/parseStatusCode';
import type { LinkContactParams } from '../../fulfillmentContext/types';
import type { PartnerContact, PartnerContactCreate } from '@shipium-corp/shipium-ts-types';
import type { StoreModel } from '../..';

export interface ContactModel {
  fetchContacts: Thunk<ContactModel, FetchAssetParams, Injections, Promise<PartnerContact[]>>;
  fetchContact: Thunk<ContactModel, string, Injections, Promise<PartnerContact>>;
  createContact: Thunk<ContactModel, PartnerContactCreate, Injections, StoreModel, Promise<PartnerContact>>;
  contactActionStart: Action<ContactModel, void>;
  lastAction?: number;
  contacts: Record<string, PartnerContact>;
  contactFailure: Action<ContactModel, ApplicationErrorOptions>;
  setContacts: Action<ContactModel, PartnerContact[]>;
  linkContact: Thunk<ContactModel, LinkContactParams, Injections, StoreModel, Promise<PartnerContact>>;
}

export const contactModel: ContactModel = {
  contacts: {},
  contactFailure: action((state, payload) => {
    console.error(payload);
  }),
  setContacts: action((state, payload) => {
    for (const val of payload) {
      state.contacts[val.contactId!] = val;
    }
  }),
  contactActionStart: action((state) => {
    state.lastAction = Date.now();
  }),
  fetchContacts: thunk(async (actions, payload, { injections }) => {
    try {
      actions.contactActionStart();
      const res = await injections.assetService.fetchContacts(payload);
      actions.setContacts(res);
      return res;
    } catch (error) {
      actions.contactFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured fetching contacts',
        asNotification: true,
      });
    }
  }),
  fetchContact: thunk(async (actions, payload, { injections }) => {
    try {
      actions.contactActionStart();
      const res = await injections.assetService.fetchContact(payload);
      actions.setContacts([res]);
      return res;
    } catch (error) {
      actions.contactFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured fetching contact',
        asNotification: true,
      });
    }
  }),
  createContact: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      actions.contactActionStart();
      const res = await injections.assetService.createContact(payload);
      actions.setContacts([res]);

      // Invalidate contact queries
      getStoreActions().queryInvalidation.invalidateQueries({
        mutationType: 'contact-create',
        entityId: res.contactId,
      });

      return res;
    } catch (error) {
      actions.contactFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured creating contact',
        asNotification: true,
      });
      return error;
    }
  }),
  linkContact: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      actions.contactActionStart();
      const res = await injections.assetService.linkContact(payload);

      // Invalidate contact queries
      getStoreActions().queryInvalidation.invalidateQueries({
        mutationType: 'contact-link',
        entityId: payload.contactId,
      });

      return res;
    } catch (error) {
      actions.contactFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured linking contact',
        asNotification: true,
      });
      return error;
    }
  }),
};
